package top.wcpe.allincore.dsl.compiler.lex

import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 手写递归式词法分析器。
 *
 * 支持的语法子集：
 * - 关键字：screen / state / defaults / root / fun / var / if / else / when
 *   / while / for / in / until / break / continue / return / try / catch / finally / await
 *   / true / false / null 以及 DSL 领域关键字
 * - 运算符：算术 `+ - * / %`、比较 `== != > < >= <=`、逻辑 `&& || !`、
 *   安全调用 `?.`、Elvis `?:`、范围 `..`、函数引用 `::`、三元 `?` `:`
 * - 字符串：双引号包裹，支持 `\\` `\"` `\n` `\t` 转义
 * - 字符串插值：`"前缀${expr}后缀"` / `"$state.count"` 拆为 STRING_PART/INTERPOLATION_START/.../INTERPOLATION_END
 * - 数字：整数或浮点（无指数）
 * - 注释：行注释 `//` 至行尾；块注释 `/* ... */` 支持嵌套
 * - 行内空白与制表符忽略；`\n` 产出 NEWLINE，连续 NEWLINE 合并
 *
 * 设计要点：
 * - 多字符运算符优先匹配更长前缀（贪心策略）
 * - 插值表达式内部的 `}` 与普通 RBRACE 通过递归扫描区分——
 *   进入 `${` 后先吞掉 `{`，深度计数从 1 开始，遇到普通 `{` 加深，遇到 `}` 在
 *   深度回到 0 时收为 INTERPOLATION_END。插值中可再次出现字符串字面量（递归）。
 */
class Lexer(
    private val source: String,
    private val sourceId: String,
) {
    /** 保留既有 JVM 单参数构造器。 */
    constructor(source: String) : this(source, "")


    private var index = 0
    private var line = 1
    private var column = 1

    /**
     * 主入口：返回 token 列表（结尾必含一枚 EOF）。
     */
    fun lex(): List<Token> {
        val tokens = mutableListOf<Token>()
        while (!isAtEnd()) {
            scanTopLevelToken(tokens)
        }
        tokens += token(TokenType.EOF, "", currentPosition())
        return tokens
    }

    // 单步扫描一个顶层 token；遇到字符串时会一次性产出多枚 token（含插值拆分）。
    private fun scanTopLevelToken(tokens: MutableList<Token>) {
        // 跳过行内空白（不含 '\n'）与注释
        while (!isAtEnd()) {
            val c = peek()
            when {
                c == ' ' || c == '\t' || c == '\r' -> advance()
                c == '/' && peekNext() == '/' -> skipLineComment()
                c == '/' && peekNext() == '*' -> skipBlockComment()
                else -> break
            }
        }
        if (isAtEnd()) return

        val startPos = currentPosition()
        val c = peek()
        when (c) {
            '\n' -> {
                advance()
                // 合并连续 NEWLINE：进一步吞吃后续的换行/空白/注释
                while (!isAtEnd()) {
                    val n = peek()
                    when {
                        n == '\n' -> advance()
                        n == ' ' || n == '\t' || n == '\r' -> advance()
                        n == '/' && peekNext() == '/' -> skipLineComment()
                        n == '/' && peekNext() == '*' -> skipBlockComment()
                        else -> break
                    }
                }
                // 若上一枚已经是 NEWLINE 则不再追加
                if (tokens.isEmpty() || tokens.last().type != TokenType.NEWLINE) {
                    tokens += token(TokenType.NEWLINE, "\n", startPos)
                }
            }
            '(' -> { advance(); tokens += token(TokenType.LPAREN, "(", startPos) }
            ')' -> { advance(); tokens += token(TokenType.RPAREN, ")", startPos) }
            '{' -> { advance(); tokens += token(TokenType.LBRACE, "{", startPos) }
            '}' -> { advance(); tokens += token(TokenType.RBRACE, "}", startPos) }
            '[' -> { advance(); tokens += token(TokenType.LBRACKET, "[", startPos) }
            ']' -> { advance(); tokens += token(TokenType.RBRACKET, "]", startPos) }
            ',' -> { advance(); tokens += token(TokenType.COMMA, ",", startPos) }
            ';' -> { advance(); tokens += token(TokenType.SEMICOLON, ";", startPos) }
            '@' -> { advance(); tokens += token(TokenType.AT, "@", startPos) }
            '+' -> { advance(); tokens += token(TokenType.PLUS, "+", startPos) }
            '-' -> {
                if (peekNext() == '>') {
                    advance(); advance()
                    tokens += token(TokenType.ARROW, "->", startPos)
                } else {
                    advance(); tokens += token(TokenType.MINUS, "-", startPos)
                }
            }
            '*' -> { advance(); tokens += token(TokenType.STAR, "*", startPos) }
            '%' -> { advance(); tokens += token(TokenType.PERCENT, "%", startPos) }
            // 贪心匹配：多字符运算符优先
            '/' -> {
                // 行注释与块注释已在前面跳过，此处 '/' 一定是除法
                advance(); tokens += token(TokenType.SLASH, "/", startPos)
            }
            '.' -> {
                if (peekNext() == '.') {
                    advance(); advance()
                    tokens += token(TokenType.RANGE, "..", startPos)
                } else {
                    advance(); tokens += token(TokenType.DOT, ".", startPos)
                }
            }
            '=' -> {
                if (peekNext() == '=') {
                    advance(); advance()
                    tokens += token(TokenType.EQ_EQ, "==", startPos)
                } else {
                    advance(); tokens += token(TokenType.EQUALS, "=", startPos)
                }
            }
            '!' -> {
                val next = peekNext()
                when {
                    next == '=' -> {
                        advance(); advance()
                        tokens += token(TokenType.NOT_EQ, "!=", startPos)
                    }
                    next == '!' -> {
                        // M10 Stage 6：`!!` 非空断言后缀
                        advance(); advance()
                        tokens += token(TokenType.BANG_BANG, "!!", startPos)
                    }
                    else -> {
                        advance(); tokens += token(TokenType.BANG, "!", startPos)
                    }
                }
            }
            '>' -> {
                if (peekNext() == '=') {
                    advance(); advance()
                    tokens += token(TokenType.GT_EQ, ">=", startPos)
                } else {
                    advance(); tokens += token(TokenType.GT, ">", startPos)
                }
            }
            '<' -> {
                if (peekNext() == '=') {
                    advance(); advance()
                    tokens += token(TokenType.LT_EQ, "<=", startPos)
                } else {
                    advance(); tokens += token(TokenType.LT, "<", startPos)
                }
            }
            '&' -> {
                if (peekNext() == '&') {
                    advance(); advance()
                    tokens += token(TokenType.AND_AND, "&&", startPos)
                } else {
                    throw LexException("非法字符 '&'（是否想用 '&&'？）", startPos)
                }
            }
            '|' -> {
                if (peekNext() == '|') {
                    advance(); advance()
                    tokens += token(TokenType.OR_OR, "||", startPos)
                } else {
                    throw LexException("非法字符 '|'（是否想用 '||'？）", startPos)
                }
            }
            ':' -> {
                if (peekNext() == ':') {
                    advance(); advance()
                    tokens += token(TokenType.DOUBLE_COLON, "::", startPos)
                } else {
                    advance(); tokens += token(TokenType.COLON, ":", startPos)
                }
            }
            '?' -> {
                val next = peekNext()
                when (next) {
                    '.' -> { advance(); advance(); tokens += token(TokenType.SAFE_CALL, "?.", startPos) }
                    ':' -> { advance(); advance(); tokens += token(TokenType.ELVIS, "?:", startPos) }
                    else -> { advance(); tokens += token(TokenType.QUESTION, "?", startPos) }
                }
            }
            '"' -> {
                if (startsWithTripleQuote()) {
                    scanTripleStringLiteral(tokens, startPos)
                } else {
                    scanStringLiteral(tokens, startPos)
                }
            }
            // M6-a：Map 字面量前缀 `#{`，井号与左大括号必须紧贴。
            // 单独的 `#` 或 `#` 后跟其它字符一律视为词法错误。
            '#' -> {
                if (peekNext() == '{') {
                    advance(); advance()
                    tokens += token(TokenType.HASH_LBRACE, "#{", startPos)
                } else {
                    throw LexException("非法字符 '#'（Map 字面量必须用 '#{' 紧贴前缀）", startPos)
                }
            }
            else -> when {
                c.isDigit() -> tokens += scanNumber(startPos)
                c == '_' || c.isLetter() -> tokens += scanIdentifierOrKeyword(startPos)
                else -> throw LexException("非法字符 '$c'", startPos)
            }
        }
    }

    // 扫描标识符 / 关键字 / 布尔 / null。
    private fun scanIdentifierOrKeyword(startPos: SourcePosition): Token {
        val start = index
        while (!isAtEnd()) {
            val c = peek()
            if (c == '_' || c.isLetterOrDigit()) advance() else break
        }
        val lexeme = source.substring(start, index)
        return when (lexeme) {
            "screen" -> token(TokenType.KW_SCREEN, lexeme, startPos)
            "hud" -> token(TokenType.KW_HUD, lexeme, startPos)
            "papi" -> token(TokenType.KW_PAPI, lexeme, startPos)
            "state" -> token(TokenType.KW_STATE, lexeme, startPos)
            "session" -> token(TokenType.KW_SESSION, lexeme, startPos)
            "persist" -> token(TokenType.KW_PERSIST, lexeme, startPos)
            "derived" -> token(TokenType.KW_DERIVED, lexeme, startPos)
            "defaults" -> token(TokenType.KW_DEFAULTS, lexeme, startPos)
            "root" -> token(TokenType.KW_ROOT, lexeme, startPos)
            "theme" -> token(TokenType.KW_THEME, lexeme, startPos)
            "animation" -> token(TokenType.KW_ANIMATION, lexeme, startPos)
            "transition" -> token(TokenType.KW_TRANSITION, lexeme, startPos)
            "keyframe" -> token(TokenType.KW_KEYFRAME, lexeme, startPos)
            "sequence" -> token(TokenType.KW_SEQUENCE, lexeme, startPos)
            "parallel" -> token(TokenType.KW_PARALLEL, lexeme, startPos)
            "stagger" -> token(TokenType.KW_STAGGER, lexeme, startPos)
            "pathTransition" -> token(TokenType.KW_PATH_TRANSITION, lexeme, startPos)
            "tracks" -> token(TokenType.KW_TRACKS, lexeme, startPos)
            "track" -> token(TokenType.KW_TRACK, lexeme, startPos)
            "frame" -> token(TokenType.KW_FRAME, lexeme, startPos)
            "component" -> token(TokenType.KW_COMPONENT, lexeme, startPos)
            "props" -> token(TokenType.KW_PROPS, lexeme, startPos)
            "children" -> token(TokenType.KW_CHILDREN, lexeme, startPos)
            "string" -> token(TokenType.KW_STRING, lexeme, startPos)
            "String" -> token(TokenType.KW_STRING, lexeme, startPos)
            "number" -> token(TokenType.KW_NUMBER, lexeme, startPos)
            "Number" -> token(TokenType.KW_NUMBER, lexeme, startPos)
            "bool" -> token(TokenType.KW_BOOL, lexeme, startPos)
            "Bool" -> token(TokenType.KW_BOOL, lexeme, startPos)
            "Boolean" -> token(TokenType.KW_BOOL, lexeme, startPos)
            "color" -> token(TokenType.KW_COLOR, lexeme, startPos)
            "Color" -> token(TokenType.KW_COLOR, lexeme, startPos)
            "canvas" -> token(TokenType.KW_CANVAS, lexeme, startPos)
            "draw" -> token(TokenType.KW_DRAW, lexeme, startPos)
            "once" -> token(TokenType.KW_ONCE, lexeme, startPos)
            "fun" -> token(TokenType.KW_FUN, lexeme, startPos)
            "Fun" -> token(TokenType.KW_FUN, lexeme, startPos)
            "Function" -> token(TokenType.KW_FUN, lexeme, startPos)
            "var" -> token(TokenType.KW_VAR, lexeme, startPos)
            "val" -> token(TokenType.KW_VAL, lexeme, startPos)
            "if" -> token(TokenType.KW_IF, lexeme, startPos)
            "else" -> token(TokenType.KW_ELSE, lexeme, startPos)
            "when" -> token(TokenType.KW_WHEN, lexeme, startPos)
            "while" -> token(TokenType.KW_WHILE, lexeme, startPos)
            "for" -> token(TokenType.KW_FOR, lexeme, startPos)
            "in" -> token(TokenType.KW_IN, lexeme, startPos)
            "until" -> token(TokenType.KW_UNTIL, lexeme, startPos)
            "break" -> token(TokenType.KW_BREAK, lexeme, startPos)
            "continue" -> token(TokenType.KW_CONTINUE, lexeme, startPos)
            "return" -> token(TokenType.KW_RETURN, lexeme, startPos)
            "try" -> token(TokenType.KW_TRY, lexeme, startPos)
            "catch" -> token(TokenType.KW_CATCH, lexeme, startPos)
            "finally" -> token(TokenType.KW_FINALLY, lexeme, startPos)
            "await" -> token(TokenType.KW_AWAIT, lexeme, startPos)
            "is" -> token(TokenType.KW_IS, lexeme, startPos)
            "data" -> token(TokenType.KW_DATA, lexeme, startPos)
            "object" -> token(TokenType.KW_OBJECT, lexeme, startPos)
            "true" -> token(TokenType.BOOLEAN, lexeme, startPos, literalValue = true)
            "false" -> token(TokenType.BOOLEAN, lexeme, startPos, literalValue = false)
            "null" -> token(TokenType.NULL, lexeme, startPos, literalValue = null)
            else -> token(TokenType.IDENTIFIER, lexeme, startPos)
        }
    }

    // 扫描整数或浮点字面量。
    private fun scanNumber(startPos: SourcePosition): Token {
        val start = index
        while (!isAtEnd() && peek().isDigit()) advance()
        if (!isAtEnd() && peek() == '.' && peekNext().isDigit()) {
            advance() // consume '.'
            while (!isAtEnd() && peek().isDigit()) advance()
        }
        val lexeme = source.substring(start, index)
        // 统一用 Double 表示数值字面量（IR 层不再区分 Long/Double）。
        // 整数字面量在 2^53 以内可精确表示；超出范围的需求当前 DSL 不存在，未来再引入位/大整数类型。
        val value: Number = lexeme.toDouble()
        return token(TokenType.NUMBER, lexeme, startPos, literalValue = value)
    }

    /**
     * 扫描以 `"` 起始的字符串字面量。
     *
     * - 无插值：产出一个 STRING token（lexeme 为已解码文本）
     * - 含插值：依次产出 INTERPOLATED_STRING_START、若干段 STRING_PART /
     *   INTERPOLATION_START..(内部 token).. INTERPOLATION_END，最后再吐一段 STRING_PART
     *   作为收尾文本（即便为空也吐，以保持 part 与表达式交替的对称结构）
     */
    private fun scanStringLiteral(tokens: MutableList<Token>, startPos: SourcePosition) {
        // 吃掉起始 '"'
        advance()
        val partBuffer = StringBuilder()
        var partStart = currentPosition()
        var startEmitted = false

        while (true) {
            if (isAtEnd()) throw LexException("未终止的字符串字面量", startPos)
            val c = peek()
            when {
                c == '"' -> {
                    // 收尾：根据是否已发过 START 决定输出形态
                    advance()
                    if (!startEmitted) {
                        // 纯字符串
                        tokens += token(
                            TokenType.STRING,
                            partBuffer.toString(),
                            startPos,
                            literalValue = partBuffer.toString(),
                        )
                    } else {
                        tokens += token(
                            TokenType.STRING_PART,
                            partBuffer.toString(),
                            partStart,
                            literalValue = partBuffer.toString(),
                        )
                    }
                    return
                }
                c == '\\' -> {
                    advance()
                    if (isAtEnd()) throw LexException("未终止的字符串转义", startPos)
                    val esc = advance()
                    partBuffer.append(
                        when (esc) {
                            'n' -> '\n'
                            't' -> '\t'
                            'r' -> '\r'
                            '"' -> '"'
                            '\\' -> '\\'
                            '$' -> '$'
                            '{' -> '{'
                            else -> throw LexException("未知的转义字符 '\\$esc'", currentPosition())
                        },
                    )
                }
                c == '$' && peekNext() == '{' -> {
                    // 插值开始：先吐前缀
                    if (!startEmitted) {
                        tokens += token(
                            TokenType.INTERPOLATED_STRING_START,
                            "\"",
                            startPos,
                            endOffset = startPos.offset + 1,
                        )
                        startEmitted = true
                    }
                    tokens += token(
                        TokenType.STRING_PART,
                        partBuffer.toString(),
                        partStart,
                        literalValue = partBuffer.toString(),
                    )
                    partBuffer.setLength(0)

                    val interpPos = currentPosition()
                    advance() // '$'
                    advance() // '{'
                    tokens += token(TokenType.INTERPOLATION_START, "\${", interpPos)

                    // 进入插值表达式：用括号深度区分插值结束 '}' 与普通 RBRACE
                    scanInterpolationBody(tokens)

                    // 插值结束后回到字符串主体
                    partStart = currentPosition()
                }
                c == '$' && isIdentifierStart(peekNext()) -> {
                    // Kotlin 风格简写：$name / $state.count
                    if (!startEmitted) {
                        tokens += token(
                            TokenType.INTERPOLATED_STRING_START,
                            "\"",
                            startPos,
                            endOffset = startPos.offset + 1,
                        )
                        startEmitted = true
                    }
                    tokens += token(
                        TokenType.STRING_PART,
                        partBuffer.toString(),
                        partStart,
                        literalValue = partBuffer.toString(),
                    )
                    partBuffer.setLength(0)

                    val interpPos = currentPosition()
                    advance() // '$'
                    tokens += token(TokenType.INTERPOLATION_START, "$", interpPos)
                    scanSimpleInterpolation(tokens)
                    tokens += token(TokenType.INTERPOLATION_END, "", currentPosition())

                    partStart = currentPosition()
                }
                c == '\n' -> throw LexException("未终止的字符串字面量", startPos)
                else -> {
                    advance()
                    partBuffer.append(c)
                }
            }
        }
    }

    private fun scanTripleStringLiteral(tokens: MutableList<Token>, startPos: SourcePosition) {
        advance(); advance(); advance()
        val contentStart = index
        var partStart = currentPosition()
        var startEmitted = false
        val parts = mutableListOf<TripleStringPart>()
        val partBuffer = StringBuilder()

        while (true) {
            if (isAtEnd()) {
                throw LexException(
                    "三引号字符串未闭合",
                    startPos,
                    DslError.E021_UNTERMINATED_TRIPLE_STRING,
                )
            }
            when {
                startsWithTripleQuote() -> {
                    val closeIndex = index
                    val closePosition = currentPosition()
                    advance(); advance(); advance()
                    val text = normalizeTripleStringPart(partBuffer.toString(), contentStart, closeIndex)
                    if (!startEmitted) {
                        tokens += token(TokenType.STRING, text, startPos, literalValue = text)
                    } else {
                        parts += TripleStringPart(text, partStart, endOffset = index)
                        emitTripleStringParts(tokens, parts)
                    }
                    if (hasNonWhitespaceBeforeNextTripleQuote()) {
                        throw LexException(
                            "三引号字符串内嵌套字面量",
                            closePosition,
                            DslError.E022_NESTED_TRIPLE_STRING_LITERAL,
                        )
                    }
                    return
                }
                peek() == '$' && peekNext() == '{' -> {
                    if (!startEmitted) {
                        tokens += token(
                            TokenType.INTERPOLATED_STRING_START,
                            "\"\"\"",
                            startPos,
                            endOffset = startPos.offset + 3,
                        )
                        startEmitted = true
                    }
                    parts += TripleStringPart(partBuffer.toString(), partStart, endOffset = index)
                    partBuffer.setLength(0)

                    val interpPos = currentPosition()
                    advance()
                    advance()
                    parts += TripleStringPart(token(TokenType.INTERPOLATION_START, "\${", interpPos))
                    scanTripleStringInterpolationBody(parts)
                    partStart = currentPosition()
                }
                peek() == '$' && isIdentifierStart(peekNext()) -> {
                    if (!startEmitted) {
                        tokens += token(
                            TokenType.INTERPOLATED_STRING_START,
                            "\"\"\"",
                            startPos,
                            endOffset = startPos.offset + 3,
                        )
                        startEmitted = true
                    }
                    parts += TripleStringPart(partBuffer.toString(), partStart, endOffset = index)
                    partBuffer.setLength(0)

                    val interpPos = currentPosition()
                    advance()
                    parts += TripleStringPart(token(TokenType.INTERPOLATION_START, "\$", interpPos))
                    scanSimpleTripleStringInterpolation(parts)
                    parts += TripleStringPart(token(TokenType.INTERPOLATION_END, "", currentPosition()))
                    partStart = currentPosition()
                }
                else -> partBuffer.append(advance())

            }
        }
    }

    private fun scanSimpleInterpolation(tokens: MutableList<Token>) {
        var identPos = currentPosition()
        tokens += scanIdentifierOrKeyword(identPos)
        while (!isAtEnd() && peek() == '.' && isIdentifierStart(peekNext())) {
            val dotPos = currentPosition()
            advance()
            tokens += token(TokenType.DOT, ".", dotPos)
            identPos = currentPosition()
            tokens += scanIdentifierOrKeyword(identPos)
        }
    }

    private fun scanSimpleTripleStringInterpolation(parts: MutableList<TripleStringPart>) {
        var identPos = currentPosition()
        parts += TripleStringPart(scanIdentifierOrKeyword(identPos))
        while (!isAtEnd() && peek() == '.' && isIdentifierStart(peekNext())) {
            val dotPos = currentPosition()
            advance()
            parts += TripleStringPart(token(TokenType.DOT, ".", dotPos))
            identPos = currentPosition()
            parts += TripleStringPart(scanIdentifierOrKeyword(identPos))
        }
    }

    /**
     * 扫描插值表达式体（已消费起始 `${` 并发出 INTERPOLATION_START）。
     *
     * 通过追踪 `{`/`}` 深度区分内部块与插值终止：深度自 0 起，遇 `{` 深度+1，
     * 遇 `}` 深度为 0 时即为插值终止，发出 INTERPOLATION_END 并返回。
     */
    private fun scanInterpolationBody(tokens: MutableList<Token>) {
        scanInterpolationBody(TokenSink.ListSink(tokens))
    }

    private fun scanTripleStringInterpolationBody(parts: MutableList<TripleStringPart>) {
        scanInterpolationBody(TokenSink.PartSink(parts))
    }

    private fun scanInterpolationBody(sink: TokenSink) {
        var depth = 0
        while (true) {
            if (isAtEnd()) throw LexException("未终止的字符串插值表达式", currentPosition())

            // 跳过空白（含换行）与行注释；插值内允许跨行
            val c = peek()
            if (c == ' ' || c == '\t' || c == '\r' || c == '\n') {
                advance()
                continue
            }
            if (c == '/' && peekNext() == '/') {
                skipLineComment()
                continue
            }
            if (c == '/' && peekNext() == '*') {
                skipBlockComment()
                continue
            }

            if (c == '}' && depth == 0) {
                val pos = currentPosition()
                advance()
                sink.add(token(TokenType.INTERPOLATION_END, "}", pos))
                return
            }

            // 字符串字面量在插值中可再次嵌套
            if (c == '"') {
                val strPos = currentPosition()
                if (startsWithTripleQuote()) {
                    scanTripleStringLiteral(sink.tokens, strPos)
                } else {
                    scanStringLiteral(sink.tokens, strPos)
                }
                sink.flushTokens()
                continue
            }

            if (c == '{') depth++
            if (c == '}') depth--

            // 复用顶层扫描分派（注意：换行已在上面单独跳过，避免发出 NEWLINE token 污染表达式流）
            scanTopLevelToken(sink.tokens)
            sink.flushTokens()
        }
    }

    // ===== 工具方法 =====

    private fun emitTripleStringParts(tokens: MutableList<Token>, parts: List<TripleStringPart>) {
        val textIndexes = parts.mapIndexedNotNull { index, part -> if (part.token == null) index else null }
        val lastTextIndex = textIndexes.last()
        val normalized = normalizeTripleStringParts(textIndexes.map { parts[it].text })
        var nextText = 0
        parts.forEachIndexed { index, part ->
            if (part.token != null) {
                tokens += part.token
                return@forEachIndexed
            }
            val text = if (index == lastTextIndex) normalized[nextText] else part.text
            nextText++
            tokens += token(
                TokenType.STRING_PART,
                text,
                part.position,
                literalValue = text,
                endOffset = part.endOffset,
            )
        }
    }

    private fun normalizeTripleStringPart(text: String, contentStart: Int, closeIndex: Int): String =
        normalizeTripleStringParts(listOf(text), contentStart, closeIndex).single()

    private fun normalizeTripleStringParts(texts: List<String>): List<String> =
        normalizeTripleStringParts(texts, 0, texts.sumOf { it.length })

    private fun normalizeTripleStringParts(texts: List<String>, contentStart: Int, closeIndex: Int): List<String> {
        val whole = texts.joinToString("")
        val block = source.getOrNull(contentStart) == '\n' && source.getOrNull(closeIndex - 1) == '\n'
        if (!block) return texts

        val trimmedWhole = whole.removePrefix("\n").removeSuffix("\n")
        val indent = trimmedWhole.lines()
            .filter { it.isNotEmpty() }
            .minOfOrNull { line -> line.takeWhile { it == ' ' || it == '\t' }.length }
            ?: 0
        return splitTripleStringText(trimmedWhole.lines().joinToString("\n") { line ->
            if (line.isEmpty()) line else line.drop(indent)
        }, texts)
    }

    private fun splitTripleStringText(text: String, parts: List<String>): List<String> {
        var offset = 0
        return parts.map { part ->
            val end = offset + part.length
            text.substring(offset.coerceAtMost(text.length), end.coerceAtMost(text.length)).also { offset = end }
        }
    }

    private fun hasNonWhitespaceBeforeNextTripleQuote(): Boolean {
        var cursor = index
        while (cursor < source.length && source[cursor].isWhitespace()) cursor++
        return cursor < source.length && cursor > index && startsWithTripleQuote(cursor)
    }

    private fun skipLineComment() {
        // 已知当前两字符为 '//'
        advance(); advance()
        while (!isAtEnd() && peek() != '\n') advance()
    }

    /** 跳过块注释 `/* ... */`，支持嵌套（用深度计数）。 */
    private fun skipBlockComment() {
        val startPos = currentPosition()
        // 已知当前两字符为 '/*'
        advance(); advance()
        var depth = 1
        while (!isAtEnd()) {
            val c = peek()
            val n = peekNext()
            when {
                c == '/' && n == '*' -> { advance(); advance(); depth++ }
                c == '*' && n == '/' -> { advance(); advance(); depth--; if (depth == 0) return }
                else -> advance()
            }
        }
        throw LexException("未终止的块注释", startPos)
    }

    private fun advance(): Char {
        val c = source[index++]
        if (c == '\n') {
            line++
            column = 1
        } else {
            column++
        }
        return c
    }

    private fun peek(): Char = if (isAtEnd()) '\u0000' else source[index]
    private fun peekNext(): Char = if (index + 1 >= source.length) '\u0000' else source[index + 1]
    private fun startsWithTripleQuote(offset: Int = index): Boolean = source.startsWith("\"\"\"", offset)
    private fun isIdentifierStart(c: Char): Boolean = c == '_' || c.isLetter()
    private fun isAtEnd(): Boolean = index >= source.length
    private fun currentPosition(): SourcePosition = SourcePosition(line, column, index, sourceId, index)

    private fun token(
        type: TokenType,
        lexeme: String,
        start: SourcePosition,
        literalValue: Any? = null,
        endOffset: Int = index,
    ): Token {
        val position = start.endingAt(endOffset)
        return Token(type, lexeme, position, literalValue)
    }

    private data class TripleStringPart(
        val text: String,
        val position: SourcePosition,
        val endOffset: Int,
        val token: Token? = null,
    ) {
        constructor(token: Token) : this("", token.position, token.range.end, token)
    }

    private sealed class TokenSink {
        abstract val tokens: MutableList<Token>
        abstract fun add(token: Token)
        open fun flushTokens() = Unit

        class ListSink(override val tokens: MutableList<Token>) : TokenSink() {
            override fun add(token: Token) {
                tokens += token
            }
        }

        class PartSink(private val parts: MutableList<TripleStringPart>) : TokenSink() {
            override val tokens: MutableList<Token> = mutableListOf()

            override fun add(token: Token) {
                parts += TripleStringPart(token)
            }

            override fun flushTokens() {
                tokens.forEach { parts += TripleStringPart(it) }
                tokens.clear()
            }
        }
    }
}
